import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Shared viewport observer manager for lazy scroll enablement.
 *
 * Limit the number of compositor layers created by slideshows by only enabling scrolling when the slideshow is in the viewport.
 * Resolves known issues with iOS Safari where too many composition layers will crash the page.
 * When a slideshow is NOT in the viewport, it has overflow: hidden (no compositor layer).
 * When a slideshow enters the viewport, the [in-viewport] attribute is added, enabling scrolling.
 */
class SlideshowViewportObserver {
  /** @type {SlideshowViewportObserver | null} */
  static #instance = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /**
   * Gets the singleton instance
   * @returns {SlideshowViewportObserver}
   */
  static getInstance() {
    if (!this.#instance) {
      this.#instance = new SlideshowViewportObserver();
    }
    return this.#instance;
  }

  /**
   * Registers a slideshow to be observed for viewport visibility
   * @param {Slideshow} slideshow - The slideshow to observe
   */
  observe(slideshow) {
    if (typeof IntersectionObserver === 'undefined') {
      slideshow.setAttribute('in-viewport', '');
      slideshow.initializeDeferredSlideshow().catch((error) => console.error(error));
      return;
    }

    if (!this.#observer) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const slideshowElement = /** @type {Slideshow} */ (entry.target);
            if (entry.isIntersecting) {
              slideshowElement.setAttribute('in-viewport', '');
              slideshowElement.initializeDeferredSlideshow().catch((error) => console.error(error));
            } else {
              slideshowElement.removeAttribute('in-viewport');
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    this.#observer.observe(slideshow);
  }

  /**
   * Unregisters a slideshow from viewport observation
   * @param {Slideshow} slideshow - The slideshow to unobserve
   */
  unobserve(slideshow) {
    this.#observer?.unobserve(slideshow);
    slideshow.removeAttribute('in-viewport');
  }
}

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  #usesDeferredInitialization = false;
  #deferredInitialized = false;
  #deferredReady = false;
  #deferredSetupResolve;
  #initializationPromise;
  #initializationInteractions;
  #connectionGeneration = 0;

  initializeDeferredSlideshow() {
    if (!this.#usesDeferredInitialization) return Promise.resolve(true);
    if (!this.isConnected) return Promise.resolve(false);
    if (this.#deferredReady) return Promise.resolve(true);
    if (this.#initializationPromise) return this.#initializationPromise;
    const generation = this.#connectionGeneration;
    const promise = Promise.resolve().then(async () => {
      if (viewTransition.current) await viewTransition.current;
      if (!this.isConnected || generation !== this.#connectionGeneration) return false;
      if (this.#deferredInitialized) return true;
      this.#deferredInitialized = true;
      const slideCount = this.slides?.length || 0;
      if (slideCount <= 1) {
        this.#setupSlideshowWithoutControls();
        this.#deferredReady = true;
      } else if (!(await this.#setupSlideshow())) {
        return false;
      }
      if (!this.isConnected || generation !== this.#connectionGeneration) return false;
      this.#initializationInteractions?.abort();
      this.#initializationInteractions = undefined;
      this.setAttribute('data-deferred-initialized', '');
      this.dispatchEvent(new CustomEvent('slideshow:initialized', { bubbles: true }));
      return true;
    }).catch((error) => {
      if (generation === this.#connectionGeneration) {
        this.#deferredInitialized = false;
        this.#deferredReady = false;
      }
      throw error;
    }).finally(() => {
      if (this.#initializationPromise === promise) this.#initializationPromise = undefined;
    });
    this.#initializationPromise = promise;
    return promise;
  }

  #initializeOnInteraction = () => {
    this.initializeDeferredSlideshow().catch((error) => console.error(error));
  };

  async connectedCallback() {
    super.connectedCallback();

    this.#connectionGeneration += 1;
    this.#usesDeferredInitialization = this.hasAttribute('data-defer-initialization');
    if (this.#usesDeferredInitialization) {
      this.#deferredInitialized = false;
      this.#deferredReady = false;
      this.#initializationPromise = undefined;
      this.#initializationInteractions = new AbortController();
      for (const event of ['pointerdown', 'focusin', 'keydown', 'click']) {
        this.addEventListener(event, this.#initializeOnInteraction, {
          capture: true,
          signal: this.#initializationInteractions.signal,
        });
      }
    }

    // Register with shared viewport observer for lazy scroll enablement.
    // This prevents iOS Safari crashes caused by too many compositor layers.
    SlideshowViewportObserver.getInstance().observe(this);

    if (this.#usesDeferredInitialization) return;

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#connectionGeneration += 1;
    this.#initializationInteractions?.abort();
    this.#initializationInteractions = undefined;
    if (this.#usesDeferredInitialization) {
      this.suspend();
      this.#dragController?.abort();
      this.#dragController = undefined;
      this.#dragging = false;
      this.removeAttribute('dragging');
      this.removeAttribute('refreshing-timeline');
      this.#pendingReorderCleanup?.();
      this.#pendingReorderCleanup = undefined;
      this.#disabled = false;
      this.#deferredInitialized = false;
      this.#deferredReady = false;
      this.#deferredSetupResolve?.(false);
      this.#deferredSetupResolve = undefined;
      this.#initializationPromise = undefined;
      this.removeAttribute('data-deferred-initialized');
    }

    // Unregister from shared viewport observer
    SlideshowViewportObserver.getInstance().unobserve(this);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    if (this.#usesDeferredInitialization) {
      this.#scroll = undefined;
      this.#resizeObserver = undefined;
      this.#visibleSlides = [];
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#usesDeferredInitialization && !this.#deferredReady) {
      event?.preventDefault();
      const generation = this.#connectionGeneration;
      if (!(await this.initializeDeferredSlideshow()) || !this.isConnected || generation !== this.#connectionGeneration) return;
    }
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;
    const deferred = this.#usesDeferredInitialization;
    const generation = this.#connectionGeneration;
    const scroll = this.#scroll;
    const isCurrent = () => !deferred || (this.isConnected && generation === this.#connectionGeneration && this.#scroll === scroll);

    // Store the actual current slide before any mutations
    const currentSlide = this.slides?.[this.current];

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;
    const { slides } = this;

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    const requestedSlideElement = slides?.[requestedIndex];
    const playVideo = event?.target?.closest('[data-play-video], .slideshow-controls__thumbnail--video-pill') != null;

    if (currentSlide === requestedSlideElement) {
      if (playVideo) {
        event?.preventDefault();
        this.dispatchEvent(
          new SlideshowSelectEvent({
            index: requestedIndex,
            previousIndex: this.current,
            userInitiated: event != null,
            trigger: 'select',
            slide: requestedSlideElement,
            id: requestedSlideElement.getAttribute('slide-id'),
            playVideo: true,
          })
        );
        this.#playSlideVideo(requestedSlideElement);
      }
      return;
    }

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll
      if (!isCurrent()) return;

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);
      const restoreOrder = () => {
        placeholder.replaceWith(targetSlide);
        if (this.#pendingReorderCleanup === restoreOrder) this.#pendingReorderCleanup = undefined;
      };
      if (deferred) this.#pendingReorderCleanup = restoreOrder;

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await scroll.finished;
        if (!isCurrent()) {
          restoreOrder();
          return;
        }
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        restoreOrder();

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });

        // Force Safari to recalculate the timeline state on timeline refresh (after loop)
        requestAnimationFrame(() => {
          if (!isCurrent()) return;
          this.setAttribute('refreshing-timeline', '');
          requestAnimationFrame(() => {
            if (!isCurrent()) return;
            this.removeAttribute('refreshing-timeline');
          });
        });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#scroll) {
      // Eagerly mark as transitioning so CSS forces content-visibility: visible on all slides
      // before scrollTo() fires. Without this, there's a 1-2 frame gap where the target slide
      // still has content-visibility: auto, causing the browser to stutter on the first frame.
      if (!instant) this.setAttribute('transitioning', '');
      this.#scroll.to(slide, { instant });
    }

    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
        playVideo,
      })
    );

    if (playVideo) {
      this.#playSlideVideo(slide);
    }
  }

  /**
   * Loads and plays deferred media in a slide after scrolling finishes.
   * @param {HTMLElement} slide
   */
  #playSlideVideo(slide) {
    const play = () => {
      const media = slide.querySelector('deferred-media, product-model');
      if (media && typeof media.showDeferredMedia === 'function') {
        media.showDeferredMedia(true);
      }
    };

    const waitForStableScroll = async () => {
      if (!this.#scroll) {
        requestAnimationFrame(play);
        return;
      }

      await this.#scroll.finished;

      // Non-adjacent slide jumps reorder the DOM after the first scroll finishes.
      while (this.hasAttribute('transitioning') || this.getAttribute('disabled') === 'true') {
        await new Promise((resolve) => {
          const observer = new MutationObserver(() => {
            if (!this.hasAttribute('transitioning') && this.getAttribute('disabled') !== 'true') {
              observer.disconnect();
              resolve(undefined);
            }
          });
          observer.observe(this, { attributes: true, attributeFilter: ['transitioning', 'disabled'] });
        });
        await this.#scroll.finished;
      }

      requestAnimationFrame(play);
    };

    waitForStableScroll();
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    if (this.#usesDeferredInitialization && !this.#deferredReady) {
      const generation = this.#connectionGeneration;
      return this.initializeDeferredSlideshow().then((initialized) => {
        if (initialized && this.isConnected && generation === this.#connectionGeneration) this.select(this.nextIndex, event, options);
      });
    }
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    if (this.#usesDeferredInitialization && !this.#deferredReady) {
      const generation = this.#connectionGeneration;
      return this.initializeDeferredSlideshow().then((initialized) => {
        if (initialized && this.isConnected && generation === this.#connectionGeneration) this.select(this.previousIndex, event, options);
      });
    }
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) {
      const text = `${value + 1}`;
      if (current.childNodes?.length !== 1 || current.firstChild?.nodeType !== 3 || current.textContent !== text) {
        current.textContent = text;
      }
    }

    if (dots) {
      dots.forEach((el, i) => {
        const selected = `${i === value}`;
        if (el.getAttribute('aria-selected') !== selected) el.setAttribute('aria-selected', selected);
        this.#restartBarFillAnimation(el, i === value);
      });
    }

    if (thumbnails) {
      thumbnails.forEach((el) => {
        const indexAttr = el.getAttribute('data-index');
        const index = indexAttr !== null ? parseInt(indexAttr, 10) : Array.from(el.parentNode.children).indexOf(el);
        const selected = `${index === value}`;
        if (el.getAttribute('aria-selected') !== selected) el.setAttribute('aria-selected', selected);
      });
    }

    if (previous) {
      const disabled = Boolean(!this.infinite && value === 0);
      if (previous.disabled !== disabled) previous.disabled = disabled;
    }
    if (next) {
      const disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
      if (next.disabled !== disabled) next.disabled = disabled;
    }
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    if (this.#usesDeferredInitialization && typeof IntersectionObserver === 'undefined') {
      return this.#measureVisibleSlides();
    }
    return this.#visibleSlides;
  }

  #measureVisibleSlides() {
    const { scroller } = this.refs;
    const vertical = this.#scroll?.axis === 'y';
    const start = vertical ? scroller.scrollTop : scroller.scrollLeft;
    const end = start + (vertical ? scroller.clientHeight : scroller.clientWidth);
    return this.slides?.filter((slide) => {
      const offset = vertical ? slide.offsetTop : slide.offsetLeft;
      const size = vertical ? slide.offsetHeight : slide.offsetWidth;
      const overlap = Math.max(0, Math.min(end, offset + size) - Math.max(start, offset));
      return size > 0 && overlap / size >= SLIDE_VISIBLITY_THRESHOLD;
    }) || [];
  }

  get previousIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides } = this;
    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;

    return current + modifier;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  /**
   * Restarts the bar pagination fill animation when the active slide changes.
   * @param {HTMLElement} control
   * @param {boolean} isSelected
   */
  #restartBarFillAnimation(control, isSelected) {
    if (!control.classList.contains('slideshow-controls__bar-item')) return;

    if (control.classList.contains('is-filling')) control.classList.remove('is-filling');

    if (!isSelected || !this.autoplay) return;

    // Force the CSS animation on ::after to restart for the newly selected bar.
    void control.offsetWidth;
    control.classList.add('is-filling');
  }

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    const deferred = this.#usesDeferredInitialization;
    const generation = this.#connectionGeneration;
    const isCurrent = () => !deferred || (this.isConnected && this.#deferredInitialized && generation === this.#connectionGeneration);
    let finishInitialization;
    const initialized = deferred ? new Promise((resolve) => {
      finishInitialization = resolve;
      this.#deferredSetupResolve = resolve;
    }) : undefined;
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      if (!isCurrent()) {
        finishInitialization?.(false);
        return;
      }
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');

      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        if (!isCurrent()) {
          finishInitialization?.(false);
          return;
        }
        if (deferred) {
          this.#deferredReady = true;
          this.#visibleSlides = this.#measureVisibleSlides();
        }
        if (this.initialSlideIndex !== 0 && initialSlideId) {
          this.select({ id: initialSlideId }, undefined, { animate: false });
          visibleSlidesAmount = 1;
        } else {
          visibleSlidesAmount = this.#updateVisibleSlides();
          if (visibleSlidesAmount === 0) {
            this.select(0, undefined, { animate: false });
            visibleSlidesAmount = 1;
          }
        }
        if (deferred) {
          if (this.#deferredSetupResolve === finishInitialization) this.#deferredSetupResolve = undefined;
          finishInitialization(true);
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;
        if (!isCurrent()) return;

        if (visibleSlidesAmount > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
    return initialized;
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const isX = axis === 'x';
    // Use scroll-offset coordinates instead of getBoundingClientRect() to avoid
    // forced reflow during the smooth-scroll animation frames.
    const scrollPos = isX ? scroller.scrollLeft : scroller.scrollTop;
    const clientSize = isX ? scroller.clientWidth : scroller.clientHeight;
    const centers = visibleSlides.map((slide) =>
      isX ? slide.offsetLeft + slide.offsetWidth / 2 : slide.offsetTop + slide.offsetHeight / 2
    );
    // Multiple visible slides: reference = left/top edge of viewport (matches original getBoundingClientRect()[axis] intent)
    // Single visible slide: reference = center of viewport (any value works, only one option)
    const referencePoint = visibleSlides.length > 1 ? scrollPos : scrollPos + clientSize / 2;
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;
  #dragController;
  #pendingReorderCleanup;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const deferred = this.#usesDeferredInitialization;
    const generation = this.#connectionGeneration;
    const scroll = this.#scroll;
    const isCurrent = () => !deferred || (this.isConnected && generation === this.#connectionGeneration && this.#scroll === scroll);
    if (deferred) this.#dragController = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      if (!isCurrent()) return;
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      if (deferred && this.#dragController === controller) this.#dragController = undefined;
      if (!isCurrent()) return;
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;
      if (!isCurrent()) return;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    if (this.#usesDeferredInitialization && typeof IntersectionObserver === 'undefined') return;
    const deferred = this.#usesDeferredInitialization;
    const generation = this.#connectionGeneration;
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (deferred && (!this.isConnected || !this.#deferredInitialized || generation !== this.#connectionGeneration)) return;
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.find(el => {
      const idx = el.getAttribute('data-index');
      return idx !== null ? parseInt(idx, 10) === index : false;
    }) || this.refs.thumbnails?.[index];

    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    if (!thumbnailsContainer.contains(selectedThumbnail)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;
    const deferred = this.#usesDeferredInitialization;
    const generation = this.#connectionGeneration;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      if (deferred && (!this.isConnected || !this.#deferredInitialized || generation !== this.#connectionGeneration)) return;
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        const hidden = `${!isVisible}`;
        if (slide.getAttribute('aria-hidden') !== hidden) slide.setAttribute('aria-hidden', hidden);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
